import { useState } from 'react';
import { company } from '../data/company';
import { categories } from '../data/categories';
import { useReveal } from '../hooks/useReveal';
import PageHero from '../components/PageHero';
import Icon, { type IconName } from '../components/Icon';

const contactCards: { icon: IconName; label: string; lines: string[]; href?: string }[] = [
  {
    icon: 'pin',
    label: 'Visit Us',
    lines: [company.address.line1, company.address.line2, company.address.line3],
  },
  {
    icon: 'mail',
    label: 'Email Us',
    lines: [company.email],
    href: `mailto:${company.email}`,
  },
  {
    icon: 'phone',
    label: 'Call Us',
    lines: [...company.phones],
    href: `tel:${company.phones[0].replace(/\s/g, '')}`,
  },
];

export default function Contact() {
  useReveal('contact');
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', company: '', interest: '', message: '' });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // No backend in this static site — compose a mailto so the enquiry reaches the team.
    const subject = encodeURIComponent(`Enquiry from ${form.name || 'website'} — ${form.interest || 'General'}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nCompany: ${form.company}\nEmail: ${form.email}\nInterest: ${form.interest}\n\n${form.message}`,
    );
    window.location.href = `mailto:${company.email}?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  return (
    <div>
      <PageHero
        eyebrow="Contact"
        title="Let's talk chemistry"
        description="Request samples, technical datasheets or a quote — our team is ready to help you find the right products and plan reliable supply."
      />

      <section className="container-page py-16 sm:py-20">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.3fr] lg:items-start">
          {/* Contact info */}
          <div className="reveal space-y-4">
            {contactCards.map((c) => {
              const body = (
                <div className="card flex items-start gap-4 p-6">
                  <span className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-primary/8 text-primary">
                    <Icon name={c.icon} className="h-6 w-6" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-secondary-dark">
                      {c.label}
                    </p>
                    {c.lines.map((l) => (
                      <p key={l} className="mt-1 text-sm font-medium text-text-primary">
                        {l}
                      </p>
                    ))}
                  </div>
                </div>
              );
              return c.href ? (
                <a key={c.label} href={c.href} className="block">
                  {body}
                </a>
              ) : (
                <div key={c.label}>{body}</div>
              );
            })}

            <div className="overflow-hidden rounded-2xl ring-1 ring-ink/5">
              <iframe
                title="Daxesh Petrochem location"
                src="https://www.google.com/maps?q=GIDC+Estate+Ankleshwar+Gujarat&output=embed"
                className="h-56 w-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form */}
          <div className="reveal card p-7 sm:p-9">
            {submitted ? (
              <div className="flex flex-col items-center py-10 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Icon name="check" className="h-8 w-8" />
                </span>
                <h2 className="mt-5 text-xl font-bold text-text-primary">Thank you!</h2>
                <p className="mt-2 max-w-sm text-sm text-text-secondary">
                  Your email client should now open with your enquiry. If it didn't, email us directly at{' '}
                  <a href={`mailto:${company.email}`} className="font-semibold text-primary">
                    {company.email}
                  </a>
                  .
                </p>
                <button type="button" onClick={() => setSubmitted(false)} className="btn-outline mt-6">
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <h2 className="text-xl font-bold text-text-primary">Send us a message</h2>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Full name" name="name" value={form.name} onChange={handleChange} required />
                  <Field label="Company" name="company" value={form.company} onChange={handleChange} />
                </div>
                <Field
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-text-primary">
                    Product of interest
                  </label>
                  <select
                    name="interest"
                    value={form.interest}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-ink/10 bg-background px-4 py-3 text-sm text-text-primary outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="">Select a product family</option>
                    {categories.map((c) => (
                      <option key={c.slug} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                    <option value="Other">Other / not sure</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-text-primary">Message</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={5}
                    required
                    placeholder="Tell us about your application, volumes and requirements…"
                    className="w-full rounded-xl border border-ink/10 bg-background px-4 py-3 text-sm text-text-primary outline-none placeholder:text-text-secondary/70 focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <button type="submit" className="btn-primary w-full sm:w-auto">
                  Send Enquiry <Icon name="arrow" className="h-4 w-4" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

interface FieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  required?: boolean;
}

function Field({ label, name, value, onChange, type = 'text', required }: FieldProps) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-semibold text-text-primary">
        {label} {required && <span className="text-secondary-dark">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full rounded-xl border border-ink/10 bg-background px-4 py-3 text-sm text-text-primary outline-none placeholder:text-text-secondary/70 focus:border-primary focus:ring-2 focus:ring-primary/20"
      />
    </div>
  );
}
